import { useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, X } from "lucide-react";
import { useTranslation } from "react-i18next";

export const AppUpdatePrompt = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const isRtl = locale === "ar" || locale === "ku";

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r: ServiceWorkerRegistration | undefined) {
      // Poll every 60s to detect new SW in background
      if (r) {
        setInterval(() => r.update(), 60 * 1000);
      }
    },
  });

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (needRefresh) setDismissed(false);
  }, [needRefresh]);

  const handleUpdate = () => {
    updateServiceWorker(true);
  };

  const handleDismiss = () => {
    setNeedRefresh(false);
    setDismissed(true);
  };

  const label = {
    message:
      locale === "ar"
        ? "يوجد تحديث جديد للتطبيق"
        : locale === "ku"
          ? "نوێکردنەوەی نوێ بۆ بەرنامەکە هەیە"
          : "A new version is available",
    update:
      locale === "ar"
        ? "تحديث الآن"
        : locale === "ku"
          ? "نوێ بکەرەوە"
          : "Update Now",
    dismiss:
      locale === "ar" ? "لاحقاً" : locale === "ku" ? "دواتر" : "Later",
  };

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return null;
  }

  if (window.location.pathname === "/full-menu") {
    return null;
  }

  return (
    <AnimatePresence>
      {needRefresh && !dismissed && (
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-5 inset-x-4 z-[9999] flex justify-center pointer-events-none"
          dir={isRtl ? "rtl" : "ltr"}
        >
          <div className="pointer-events-auto w-full max-w-sm bg-primary text-bodyBg rounded-2xl shadow-2xl shadow-primary/30 flex items-center gap-3 px-4 py-3.5">
            {/* Icon */}
            <div className="shrink-0 bg-white/15 rounded-full p-2">
              <RefreshCw className="size-5 animate-spin [animation-duration:2s]" />
            </div>

            {/* Text */}
            <p className="flex-1 text-sm font-semibold leading-snug">
              {label.message}
            </p>

            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleUpdate}
                className="bg-white text-primary text-xs font-bold px-3 py-1.5 rounded-full hover:bg-white/90 transition-colors"
              >
                {label.update}
              </button>
              <button
                onClick={handleDismiss}
                className="text-white/70 hover:text-white transition-colors p-1"
                aria-label="dismiss"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
